import React, { useEffect, useMemo, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "../../components/shared/Navbar/Navbar";
import Footer from "../../components/shared/Footer/Footer";
import "./AllSolution.css";
import SolutionDetailsHero from "../../components/pages/SolutionDetails/SolutionDetailsHero/SolutionDetailsHero";
import SolutionDetailsDescription from "../../components/pages/SolutionDetails/SolutionDetailsDescription/SolutionDetailsDescription";
import SolutionDetailsFeatures from "../../components/pages/SolutionDetails/SolutionDetailsFeatures/SolutionDetailsFeatures";
import Hospitality from "../../components/pages/Home/Hospitality/Hospitality";
import SolutionDetailsProducts from "../../components/pages/SolutionDetails/SolutionDetailsProducts/SolutionDetailsProducts";
import SolutionDetailsDownloads from "../../components/pages/SolutionDetails/SolutionDetailsDownloads/SolutionDetailsDownloads";
import AllSolutionsRequestNewProject from "../../components/pages/AllSolutions/AllSolutionsRequestNewProject/AllSolutionsRequestNewProject";
import Partners from "../../components/pages/About/Partners/Partners";
import { getSolutionDetailBySlug } from "../../data/solutionsDetailData";
import {
  findPageSection,
  unwrapSectionData,
} from "../../utils/aboutStartNewProjectSection";
import { filterPdfRowsByProductSlug, mapPublicProductPdfs, pickPublicPdfArray } from "../../utils/productPdfs";
import {
  usePageContent,
  usePublicSolutionByKey,
} from "../../hooks/usePublicQueries";

const SolutionDetails = () => {
  const { i18n } = useTranslation();
  const { solutionSlug } = useParams();
  const fallbackSolution = getSolutionDetailBySlug(solutionSlug);
  const { data: apiSolution } = usePublicSolutionByKey(solutionSlug, i18n.language);
  const { data: homeContent } = usePageContent("home", i18n.language);
  const [activeDescriptionTab, setActiveDescriptionTab] = useState(0);

  const solution = useMemo(() => {
    if (!apiSolution) return fallbackSolution;
    const solutionPdfHints = [solutionSlug, apiSolution?.solutionKey, fallbackSolution?.solutionKey];
    const mergedSolutionPdfs = mapPublicProductPdfs(
      filterPdfRowsByProductSlug(pickPublicPdfArray(apiSolution, fallbackSolution), ...solutionPdfHints)
    );

    // Prefer API structured tabs/features; do not let static string tabs overwrite them.
    const descriptionTabs = Array.isArray(apiSolution.descriptionTabs)
      ? apiSolution.descriptionTabs
      : fallbackSolution?.descriptionTabs;

    return {
      ...(fallbackSolution || {}),
      ...apiSolution,
      descriptionTabs,
      features: Array.isArray(apiSolution.features) ? apiSolution.features : fallbackSolution?.features,
      products: Array.isArray(apiSolution.products) ? apiSolution.products : fallbackSolution?.products,
      cardIcon: apiSolution?.cardIcon || fallbackSolution?.cardIcon || "",
      logo: apiSolution?.logo || fallbackSolution?.logo || "",
      logoUrl: apiSolution?.logoUrl || fallbackSolution?.logoUrl || "",
      heroLogo: apiSolution?.heroLogo || fallbackSolution?.heroLogo || "",
      heroLogoUrl: apiSolution?.heroLogoUrl || fallbackSolution?.heroLogoUrl || "",
      pdfs: mergedSolutionPdfs,
    };
  }, [apiSolution, fallbackSolution, solutionSlug]);

  useEffect(() => {
    setActiveDescriptionTab(0);
  }, [solution?.id, solution?.solutionKey, solution?.slug]);

  const hospitalitySection = findPageSection(homeContent, "hospitality");

  if (!solution) {
    return <Navigate to="/allsolutions" replace />;
  }

  return (
    <div className="allsolution">
      <div className="allsolution-content-guide">
        <Navbar theme="dark" />
        <SolutionDetailsHero solution={solution} />
        <SolutionDetailsDescription
          solution={solution}
          activeTabIndex={activeDescriptionTab}
          onActiveTabChange={setActiveDescriptionTab}
        />
        <SolutionDetailsFeatures solution={solution} activeTabIndex={activeDescriptionTab} />
        <Hospitality content={unwrapSectionData(hospitalitySection)} />
        <SolutionDetailsProducts solution={solution} />
        <SolutionDetailsDownloads
          solution={solution}
          items={(solution?.pdfs || []).map((p) => ({
            id: p.id,
            title: p.title,
            href: p.href,
          }))}
        />
        <AllSolutionsRequestNewProject />
        <Partners />
      </div>
      <Footer />
    </div>
  );
};

export default SolutionDetails;
